import {
  AloftCount,
  LatitudeLongitude,
  ToolkitProperty,
  ToolkitPropertyFeatureKey,
  isObject,
  isString,
} from '@aloft/toolkit-shared';
import { Row as ReactTableRow, SortingFn as ReactTableSortingFn } from '@tanstack/react-table';

import { ReactNode } from 'react';

import { usePinnedColumnStyles } from './usePinnedColumnStyles.js';

export type TPTableSelector<T, U> = (data: T) => U | undefined;
export type TPTableSortBy<T> = (a: T, b: T, subjectProperty?: ToolkitProperty) => number;
export type TPTableSortingState = { [id: string]: { desc: boolean } };
export type TPTableClickRow<T> = (data: T) => void;
export type TPTableSelectRow<T> = (data: T, isSelected: boolean) => void;

// System-defined TP column types

type TPColumnDef<T> = {
  title: string;
  selector: TPTableSelector<ToolkitProperty, T>;
  toString: (value: T, subjectProperty?: ToolkitProperty) => string;
  sortBy?: TPTableSortBy<T>;
  size?: number;
};

export type TPColumnKey = ToolkitPropertyFeatureKey;

export function isTPColumnKey(x: unknown): x is TPColumnKey {
  return isString(x);
}

type SystemAccessorTPColumnKeyToType = {
  distance: Partial<LatitudeLongitude>;
  baths: {
    aboveGradeFullBathCount?: AloftCount;
    aboveGradeHalfBathCount?: AloftCount;
    aboveGradeThreeQuarterBathCount?: AloftCount;
  };
};

export type SystemAccessorTPColumnKey = keyof SystemAccessorTPColumnKeyToType;

export function isSystemAccessorTPColumnKey(x: unknown): x is SystemAccessorTPColumnKey {
  const keys: SystemAccessorTPColumnKey[] = ['distance', 'baths'];
  return isString(x) && keys.includes(x as SystemAccessorTPColumnKey);
}

export type SystemAccessorTPColumnDefs = {
  [K in SystemAccessorTPColumnKey]: TPColumnDef<SystemAccessorTPColumnKeyToType[K]>;
};

export type SystemDisplayTPColumnKey = 'isSelected' | 'moreMenu';

export type SystemTPColumnKey = SystemAccessorTPColumnKey | SystemDisplayTPColumnKey;

// User-defined column types

/** User-defined column for a table with `ToolkitProperty`-dependent data (i.e., table data that aren't `ToolkitProperty`s but use `ToolkitProperty`s). */
export type CustomTPColumnDef<T, U> = {
  id: `custom$${string}`;
  title: string;
  selector: TPTableSelector<T, U>;
  toCell: (value: U) => ReactNode;
  sortBy?: TPTableSortBy<U>;
  __dangerOverrideSortBy?: ReactTableSortingFn<T>;
};

export function isCustomTPColumnDef<T, U>(x: unknown): x is CustomTPColumnDef<T, U> {
  return isObject(x) && 'id' in x && isString(x.id) && x.id.startsWith('custom$');
}

// Component types

/** Valid columns for tables with `ToolkitProperty`-dependent data. */
export type CustomTPColumn<T> = TPColumnKey | SystemAccessorTPColumnKey | CustomTPColumnDef<T, any>;

/** Prop to configure `TPColumns` for tables with `ToolkitProperty`-dependent data. */
export type TPColumnConfig<Data, Value> = {
  toValue: (selector: (property: ToolkitProperty) => unknown, data: Data) => Value;
  toCell: (props: { value: Value; toString: (x: unknown) => string }) => string | React.ReactNode;
  toCompareValues: (rowA: Value, rowB: Value) => [unknown | undefined, unknown | undefined];
};

export type RowProps<T> = {
  isSelected: boolean | null; // necessary for memoization prop comparison
  row: ReactTableRow<T>;
  id?: string;
  activeRowIndex: number | null;
  isHovered?: boolean;
  shouldPinFirstRow: boolean | number;
  handleRowClick: (row: ReactTableRow<T>) => void;
  handleRowHover?: (row: ReactTableRow<T>, isHovered: boolean) => void;
  enableHoverStyle: boolean;
  pinnedColumnStyles: ReturnType<typeof usePinnedColumnStyles>;
  customBackgroundColor?: string;
  animateEnter?: boolean;
};
